import { Zap, ArrowUpCircle } from 'lucide-react';

interface CreditsBadgeProps {
  credits: number;
  plan: 'free' | 'starter' | 'professional' | 'agency';
  compact?: boolean;  // For header vs banner usage
  onUpgrade?: () => void;
}

// Plan-specific styling (matching WixDashboardPage.tsx colors, adapted for light backgrounds)
const planStyles = {
  free: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    badgeBg: 'bg-slate-200',
    badgeText: 'text-slate-700',
    label: 'Free',
  },
  starter: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    badgeBg: 'bg-blue-200',
    badgeText: 'text-blue-700',
    label: 'Starter',
  },
  professional: {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    badgeBg: 'bg-violet-200',
    badgeText: 'text-violet-700',
    label: 'Pro',
  },
  agency: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-200',
    badgeText: 'text-amber-700',
    label: 'Agency',
  },
};

export function CreditsBadge({ credits, plan, compact = false, onUpgrade }: CreditsBadgeProps) {
  const style = planStyles[plan];
  const showUpgrade = (plan === 'free' || credits === 0) && onUpgrade;

  if (compact) {
    // Compact mode for header: Icon + number + small badge
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 ${style.bg} border ${style.border} rounded-lg`}>
        <div className={`p-1 ${style.iconBg} rounded`}>
          <Zap className={`w-4 h-4 ${style.iconColor}`} />
        </div>
        <span className="text-sm font-semibold text-slate-900">{credits}</span>
        <div className={`px-2 py-0.5 ${style.badgeBg} rounded-full`}>
          <span className={`text-xs font-medium ${style.badgeText}`}>{style.label}</span>
        </div>
      </div>
    );
  }

  // Full mode for banner: Multi-line display with upgrade option
  return (
    <div className={`${style.bg} border ${style.border} rounded-xl p-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 ${style.iconBg} rounded-lg`}>
            <Zap className={`w-5 h-5 ${style.iconColor}`} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase font-medium">Available Credits</p>
            <p className="text-2xl font-bold text-slate-900">{credits}</p>
          </div>
        </div>

        <div className="text-right flex flex-col items-end gap-2">
          <div className={`px-3 py-1 ${style.badgeBg} rounded-full`}>
            <span className={`text-xs font-medium ${style.badgeText}`}>{style.label} Plan</span>
          </div>

          {showUpgrade && (
            <button
              onClick={onUpgrade}
              className="flex items-center gap-1 px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-md transition-colors"
            >
              <ArrowUpCircle className="w-3 h-3" />
              <span>Upgrade</span>
            </button>
          )}
        </div>
      </div>

      {credits === 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <p className="text-sm text-slate-600">
            You've used all your credits. {showUpgrade ? 'Upgrade to continue generating content!' : 'Purchase more credits to continue.'}
          </p>
        </div>
      )}
    </div>
  );
}
